/**
 * Real-time transfer queue updates via WebSocket.
 * Listens for `transfer_queue_update` messages and invokes onTransferQueueEvent.
 * Modelled on useOrderWebSocket – shares the same WebSocket URL & subscription pattern.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const PING_INTERVAL_MS = 30_000;
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 20;
const NORMAL_CLOSE_CODE = 1000;

export interface TransferQueueWsEvent {
    action: 'created' | 'updated' | 'deleted';
    transferQueue: Record<string, any>;
    timestamp?: string;
}

export interface UseTransferQueueWebSocketOptions {
    userId?: string;
    jobLocations?: string[];
    onTransferQueueEvent?: (event: TransferQueueWsEvent) => void;
    enabled?: boolean;
}

export interface UseTransferQueueWebSocketReturn {
    isConnected: boolean;
    reconnectAttempts: number;
}

function getWsBaseUrl(): string {
    const env = import.meta.env.VITE_WS_URL;
    if (env?.trim()) return env.trim();
    return 'wss://xg615ecipc.execute-api.ca-central-1.amazonaws.com/dev/';
}

function buildWsUrl(userId?: string, jobLocations?: string[]): string {
    const base = getWsBaseUrl();
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (jobLocations?.length) params.set('jobLocations', jobLocations.join(','));
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
}

function isTransferQueueMessage(
    msg: unknown
): msg is { action: string; data: { transferQueue: Record<string, any>; action: string }; timestamp?: string } {
    if (typeof msg !== 'object' || msg === null) return false;
    const m = msg as Record<string, any>;
    return m.action === 'transfer_queue_update' || m.action === 'transfer_queue_created';
}

export function useTransferQueueWebSocket({
    userId,
    jobLocations,
    onTransferQueueEvent,
    enabled = true,
}: UseTransferQueueWebSocketOptions): UseTransferQueueWebSocketReturn {
    const [isConnected, setIsConnected] = useState(false);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);

    const wsRef = useRef<WebSocket | null>(null);
    const pingIntervalRef = useRef<number | null>(null);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const enabledRef = useRef(enabled);
    const onEventRef = useRef(onTransferQueueEvent);
    const jobLocationsRef = useRef(jobLocations);

    useEffect(() => { onEventRef.current = onTransferQueueEvent; }, [onTransferQueueEvent]);
    useEffect(() => { enabledRef.current = enabled; }, [enabled]);
    useEffect(() => { jobLocationsRef.current = jobLocations; }, [jobLocations]);

    const clearPingInterval = useCallback(() => {
        if (pingIntervalRef.current !== null) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
    }, []);

    const clearReconnectTimer = useCallback(() => {
        if (reconnectTimerRef.current !== null) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    }, []);

    const sendSubscribe = useCallback((ws: WebSocket, locations: string[]) => {
        if (ws.readyState === WebSocket.OPEN && locations.length > 0) {
            ws.send(JSON.stringify({ type: 'subscribe', jobLocations: locations }));
        }
    }, []);

    const scheduleReconnect = useCallback(() => {
        if (!enabledRef.current) return;
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) return;

        clearReconnectTimer();
        const attempt = reconnectAttemptsRef.current;
        const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
        reconnectAttemptsRef.current += 1;
        setReconnectAttempts(reconnectAttemptsRef.current);
        reconnectTimerRef.current = setTimeout(() => { connectRef.current(); }, delay);
    }, [clearReconnectTimer]);

    const connect = useCallback(() => {
        if (!enabledRef.current) return;
        if (wsRef.current?.readyState === WebSocket.OPEN) return;
        if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

        const url = buildWsUrl(userId, jobLocationsRef.current ?? undefined);

        try {
            const ws = new WebSocket(url);
            wsRef.current = ws;

            ws.onopen = () => {
                setIsConnected(true);
                reconnectAttemptsRef.current = 0;
                setReconnectAttempts(0);

                const locations = jobLocationsRef.current;
                if (locations?.length) sendSubscribe(ws, locations);

                clearPingInterval();
                pingIntervalRef.current = window.setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ type: 'ping' }));
                    }
                }, PING_INTERVAL_MS);
            };

            ws.onmessage = (event: MessageEvent) => {
                try {
                    const msg = JSON.parse(event.data as string) as unknown;
                    if (isTransferQueueMessage(msg)) {
                        const payload = msg.data;
                        const wsEvent: TransferQueueWsEvent = {
                            action: (payload?.action as TransferQueueWsEvent['action']) ?? 'updated',
                            transferQueue: payload?.transferQueue ?? payload ?? {},
                            timestamp: msg.timestamp,
                        };
                        onEventRef.current?.(wsEvent);
                    }
                } catch {
                    // ignore non-JSON or irrelevant messages
                }
            };

            ws.onclose = (event: CloseEvent) => {
                setIsConnected(false);
                clearPingInterval();
                wsRef.current = null;
                if (enabledRef.current && event.code !== NORMAL_CLOSE_CODE) {
                    scheduleReconnect();
                }
            };

            ws.onerror = () => {
                // reconnect handled in onclose
            };
        } catch {
            scheduleReconnect();
        }
    }, [userId, clearPingInterval, sendSubscribe, scheduleReconnect]);

    const connectRef = useRef(connect);
    connectRef.current = connect;

    // Re-subscribe when jobLocations change on an open connection
    useEffect(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN && jobLocations?.length) {
            sendSubscribe(wsRef.current, jobLocations);
        }
    }, [jobLocations, sendSubscribe]);

    // Lifecycle: connect / disconnect
    useEffect(() => {
        if (!enabled) {
            clearPingInterval();
            clearReconnectTimer();
            if (wsRef.current) {
                wsRef.current.onclose = null;
                wsRef.current.close(NORMAL_CLOSE_CODE, 'Disabled');
                wsRef.current = null;
            }
            setIsConnected(false);
            return;
        }

        connect();

        return () => {
            clearPingInterval();
            clearReconnectTimer();
            if (wsRef.current) {
                wsRef.current.onclose = null;
                wsRef.current.close(NORMAL_CLOSE_CODE, 'Unmount');
                wsRef.current = null;
            }
            setIsConnected(false);
        };
    }, [enabled, userId, connect, clearPingInterval, clearReconnectTimer]);

    return { isConnected, reconnectAttempts };
}
